import { getAgentProfile } from "./agentProfile";
import { getBrokerProfile } from "./brokerProfile";
import { getSession } from "../auth/authService";
import { ensureJourneyDb, getById } from "../db";

function formatAddress(profile = {}) {
  const line = [profile.office_city, profile.office_state, profile.office_zip].filter(Boolean).join(", ");
  return [profile.office_address, line].filter(Boolean).join("\n");
}

function fromBrokerProfile(profile, session) {
  if (!profile) return null;
  const address = formatAddress(profile);
  const has =
    profile.office_name ||
    profile.broker_name ||
    profile.broker_phone ||
    profile.broker_email ||
    profile.license ||
    profile.brokerage_license ||
    address;
  if (!has && !session?.office_name) return null;
  return {
    kind: "broker",
    heading: "Broker",
    name: profile.broker_name || session?.name || "",
    title: profile.broker_title || "Broker of record",
    office_name: profile.office_name || session?.office_name || "",
    phone: profile.broker_phone || profile.office_phone || "",
    email: profile.broker_email || profile.office_email || session?.email || "",
    website: profile.website || "",
    address,
    locations: profile.locations || "",
    license: profile.license || "",
    brokerage_license: profile.brokerage_license || "",
    license_expires: profile.license_expires || "",
    extra: profile.extra || "",
  };
}

function fromAgentProfile(profile, session) {
  if (!profile) return null;
  const address = formatAddress(profile);
  const has =
    profile.office_name ||
    profile.agent_name ||
    profile.agent_phone ||
    profile.agent_email ||
    profile.dre_license ||
    address;
  if (!has && !session?.name) return null;
  return {
    kind: "agent",
    heading: "Agent",
    name: profile.agent_name || session?.name || "",
    title: profile.agent_title || "Sales associate",
    office_name: profile.office_name || session?.office_name || "",
    phone: profile.agent_phone || profile.office_phone || "",
    email: profile.agent_email || session?.email || "",
    website: profile.website || "",
    address,
    locations: "",
    license: profile.dre_license || "",
    brokerage_license: "",
    license_expires: profile.dre_expires || "",
    extra: profile.broker_of_record ? `Broker of record: ${profile.broker_of_record}` : "",
  };
}

function brokerIdForAgent(agentId) {
  if (!agentId) return null;
  try {
    ensureJourneyDb();
    return getById("users", agentId)?.brokerage_id || null;
  } catch {
    return null;
  }
}

export function resolveOfficeContact(session = getSession()) {
  const cards = [];
  try {
    if (session?.role === "broker" || session?.role === "admin") {
      const broker = fromBrokerProfile(getBrokerProfile(session.userId, session), session);
      if (broker) cards.push(broker);
      return cards;
    }

    if (session?.role === "agent") {
      const agent = fromAgentProfile(getAgentProfile(session.userId, session), session);
      if (agent) cards.push(agent);
      const brokerageId = brokerIdForAgent(session.userId);
      if (brokerageId) {
        const broker = fromBrokerProfile(getBrokerProfile(brokerageId), null);
        if (broker) cards.push(broker);
      }
      return cards;
    }

    if (session?.role === "buyer") {
      const buyers = JSON.parse(localStorage.getItem("dh_buyers") || "[]");
      const buyer = buyers.find((row) => row.id === session.userId);
      if (buyer?.agentId) {
        const agent = fromAgentProfile(getAgentProfile(buyer.agentId), null);
        if (agent) cards.push(agent);
        const brokerageId = brokerIdForAgent(buyer.agentId);
        if (brokerageId) {
          const broker = fromBrokerProfile(getBrokerProfile(brokerageId), null);
          if (broker) cards.push(broker);
        }
      }
      return cards;
    }

    if (session?.role === "seller") {
      ensureJourneyDb();
      const user = getById("users", session.userId);
      if (user?.id) {
        const sellers = JSON.parse(localStorage.getItem("dh_sellers") || "[]");
        const seller = sellers.find((row) => row.id === user.id);
        const agentId = seller?.agentId;
        if (agentId) {
          const agent = fromAgentProfile(getAgentProfile(agentId), null);
          if (agent) cards.push(agent);
        }
      }
      if (user?.brokerage_id) {
        const broker = fromBrokerProfile(getBrokerProfile(user.brokerage_id), null);
        if (broker) cards.push(broker);
      }
      return cards;
    }
  } catch {
    return cards;
  }
  return cards;
}
